/**
 * Route Configuration File
 *
 * This file centralizes all route definitions for the application.
 *
 * React Router v6+ Best Practices Applied:
 * - Lazy loading with React.lazy() for code splitting
 * - Semantic, SEO-friendly URLs
 * - Centralized route management
 * - Easy to maintain and extend
 * - Automatic sitemap generation support
 *
 * Route Configuration Structure:
 * - path: The URL path (SEO-friendly, lowercase, hyphenated)
 * - component: Lazy-loaded component
 * - exact: Whether to match exactly (default: true)
 * - protected: Whether route requires authentication
 * - adminOnly: Whether route requires admin privileges
 * - seo: SEO metadata for the route
 */

import { lazy } from "react";

// Lazy-loaded components for code splitting
// This splits your bundle into smaller chunks, loading only what's needed
const GuidesPage = lazy(() => import("../guides/GuidesPage"));
const GuideArticlePage = lazy(() => import("../guides/GuideArticlePage"));
const HeroSection = lazy(() => import("../components/header/HeroSection"));
const ProductsPage = lazy(() => import("../services/ProductsPage"));
const QuotePage = lazy(() => import("../components/products/QuotePage"));
const AutoQuotingForm = lazy(
  () => import("../components/products/AutoQuotingForm"),
);
const AutoQuoteConfirmation = lazy(
  () => import("../components/products/AutoQuoteConfirmation"),
);
const GeneralLiabilityForm = lazy(
  () => import("../components/products/GeneralLiabilityForm"),
);
const RentersInsurance = lazy(() => import("../renters/RentersInsurance"));
const AboutUs = lazy(() => import("../about/AboutUs"));
const LocationPage = lazy(() => import("../location/LocationPage"));
const OpenHours = lazy(() => import("../location/OpenHours"));
const ContactUs = lazy(() => import("../contact/ContactUs"));
const Appointment = lazy(() => import("../appointment/Appointment"));
const KarmelMallInsurance = lazy(
  () => import("../karmelMall/KarmelMallInsurance"),
);
const MinneapolisInsuranceAgency = lazy(
  () => import("../minneapolis/MinneapolisInsuranceAgency"),
);
const SomaliInsuranceAgent = lazy(
  () => import("../minneapolis/SomaliInsuranceAgent"),
);
const Privacy = lazy(() => import("../components/policies/Privacy"));
const Terms = lazy(() => import("../components/policies/Terms"));
const Accessibility = lazy(
  () => import("../components/policies/Accessibility"),
);
const LoginPage = lazy(() => import("../middleware/LoginPage"));
const Logout = lazy(() => import("../middleware/Logout"));
const Dashboard = lazy(() => import("../dashboard/Dashboard"));
const NotFound = lazy(() => import("../utils/NotFound"));
const CityInsurancePage = lazy(() => import("../city/CityInsurancePage"));
const AutoInsuranceCityPage = lazy(
  () => import("../autoInsurance/AutoInsuranceCityPage"),
);
const InsuranceCarriersPage = lazy(
  () => import("../carriers/InsuranceCarriers"),
);
const HomeInsurance = lazy(() => import("../homeInsurance/HomeInsurance"));
const CommercialInsurance = lazy(
  () => import("../commercial/CommercialInsurance"),
);
const TruckingInsurance = lazy(() => import("../trucking/TruckingInsurance"));
const GeneralBusinessLiabilityForm = lazy(
  () => import("../components/products/GeneralBusinessLiabilityForm"),
);
const CommercialTruckQuoteForm = lazy(
  () => import("../trucking/CommercialTruckQuoteForm"),
);
const LifeInsurance = lazy(() => import("../lifeInsurance/LifeInsurance"));

/**
 * Public Routes Configuration
 * These routes are accessible to all users
 */
export const publicRoutes = [
  {
    path: "/",
    component: HeroSection,
    exact: true,
    seo: {
      title: "Insurance Agency Minneapolis MN | Kaphy Insurance Agency",
      description:
        "Independent insurance agency in Minneapolis, MN serving the Twin Cities. Compare auto, renters & commercial insurance from top carriers — free quotes.",
      keywords:
        "insurance agency Minneapolis, insurance agency in Minneapolis, Minneapolis insurance agency, independent insurance agency Minneapolis, independent insurance agent Minneapolis, insurance agent Minneapolis MN, insurance broker Minneapolis, local insurance agency Minneapolis, auto insurance Minneapolis, car insurance Minneapolis, renters insurance Minneapolis, commercial insurance Minneapolis, Minnesota insurance agency",
      priority: 1.0,
      changefreq: "daily",
    },
  },
  {
    path: "/our-products",
    component: ProductsPage,
    seo: {
      title:
        "Insurance Products Minneapolis MN | Auto, Home, Life, Renters & Business | Kaphy Insurance",
      description:
        "Compare auto, home, renters, life & commercial insurance in Minneapolis, MN. A+ rated independent agency — shop multiple carriers, get free quotes, and save on coverage today.",
      keywords:
        "insurance products Minneapolis MN, car insurance Minneapolis MN, auto insurance quotes Minneapolis, home insurance Minneapolis MN, renters insurance Minneapolis, life insurance Minneapolis MN, business insurance Minneapolis, commercial insurance coverage Minnesota, general liability insurance Minneapolis, workers compensation insurance Minnesota, life insurance Minnesota, independent insurance agency Minneapolis, affordable insurance Minneapolis, compare insurance rates MN, insurance agency near me Minneapolis",
      priority: 0.9,
      changefreq: "monthly",
    },
  },
  {
    path: "/get-quote-today",
    component: QuotePage,
    seo: {
      title:
        "Get Free Insurance Quote Minneapolis MN | Auto, Home, Life & More | Kaphy Insurance Agency",
      description:
        "Request a free insurance quote online and compare auto, home, renters, life & commercial insurance rates in Minneapolis, MN. Fast, no-obligation quotes from a trusted independent agency.",
      keywords:
        "get insurance quote Minneapolis MN, compare insurance rates Minnesota, free auto insurance quote Minneapolis, home insurance quote Minneapolis, renters insurance quote MN, life insurance quote Minneapolis MN, commercial insurance quote Minneapolis, save money on insurance Minnesota, request a quote online MN, insurance quote near me Minneapolis, best insurance rates Minneapolis MN, life insurance quote Minnesota",
      priority: 0.9,
      changefreq: "weekly",
    },
  },
  {
    path: "/commercial-truck-insurance-quote",
    component: CommercialTruckQuoteForm,
    seo: {
      title:
        "Commercial Truck Insurance Quote | Kaphy Insurance Agency Minnesota",
      description:
        "Request a commercial truck insurance quote for your Minnesota trucking operation. Kaphy Insurance Agency helps owner-operators and fleets compare coverage from multiple carriers.",
      keywords:
        "commercial truck insurance quote Minnesota, trucking insurance application, owner operator insurance quote Minnesota",
      priority: 0.0,
      changefreq: "yearly",
      robots: "noindex, follow",
      includeInSitemap: false,
    },
  },
  {
    path: "/get-business-liability-quote",
    component: GeneralBusinessLiabilityForm,
    seo: {
      title:
        "General Business Liability Insurance Quote | Kaphy Insurance Agency Minneapolis",
      description:
        "Request a free General Business Liability insurance quote online. Kaphy Insurance Agency helps Minneapolis-area businesses get the coverage they need. No obligation, 24-hour response.",
      keywords:
        "general business liability insurance Minneapolis, business liability quote Minnesota, commercial liability insurance Minneapolis, small business insurance quote MN",
      priority: 0.85,
      changefreq: "weekly",
      robots: "noindex, follow",
      includeInSitemap: false,
    },
  },
  {
    path: "/get-general-liability",
    component: GeneralLiabilityForm,
    seo: {
      title:
        "Errors & Omissions / E&O Insurance Quote | Kaphy Insurance Agency Minneapolis",
      description:
        "Request a free Errors & Omissions (E&O) insurance quote online. Kaphy Insurance Agency helps Minneapolis-area small businesses get E&O coverage. No obligation.",
      keywords:
        "general liability insurance quote Minneapolis, E&O insurance quote Minnesota, errors and omissions insurance Minneapolis, small business insurance quote MN, GL insurance quote Minneapolis, business liability insurance Minnesota",
      priority: 0.85,
      changefreq: "weekly",
      robots: "noindex, follow",
      includeInSitemap: false,
    },
  },
  {
    path: "/auto-quote-form",
    component: AutoQuotingForm,
    seo: {
      title: "Auto Insurance Quote Form | Get Car Insurance Quote Minneapolis",
      description:
        "Fill out our quick auto insurance quote form to receive competitive car insurance rates. Get personalized coverage options from multiple carriers.",
      keywords:
        "auto insurance quote form, car insurance quote, auto quote online, Minneapolis car insurance",
      priority: 0.8,
      changefreq: "weekly",
      robots: "noindex, follow", // Form pages typically don't need to be indexed
      includeInSitemap: false, // Exclude from sitemap — noindex pages must not appear in sitemap
    },
  },
  {
    path: "/auto-quote-confirmation",
    component: AutoQuoteConfirmation,
    seo: {
      title: "Quote Request Received | Kaphy Insurance Agency",
      description:
        "Thank you for requesting an auto insurance quote. We will contact you shortly with competitive rates.",
      keywords: "quote confirmation, insurance quote received",
      priority: 0.3,
      changefreq: "yearly",
      robots: "noindex, nofollow", // Confirmation pages should not be indexed
      includeInSitemap: false, // Exclude from sitemap — noindex pages must not appear in sitemap
    },
  },
  {
    path: "/renters-insurance",
    component: RentersInsurance,
    seo: {
      title:
        "Renters Insurance Minneapolis MN | Get a Quote | Kaphy Insurance Agency",
      description:
        "Get a renters insurance quote in Minneapolis, MN through Kaphy Insurance Agency. Affordable apartment & tenant coverage — start your secure online quote today.",
      keywords:
        "renters insurance Minneapolis MN, renters insurance Minneapolis, Minneapolis renters insurance, renters insurance agency Minneapolis, renters insurance quote Minneapolis, affordable renters insurance Minneapolis, apartment insurance Minneapolis, tenant insurance Minneapolis, renters insurance near me, Minnesota renters insurance",
      priority: 0.8,
      changefreq: "weekly",
    },
  },
  {
    path: "/karmel-mall-insurance-agency",
    component: KarmelMallInsurance,
    seo: {
      title:
        "Karmel Mall Insurance Agency | Auto & Renters Insurance Minneapolis MN",
      description:
        "Kaphy Insurance Agency helps Minnesota drivers, renters, homeowners, and businesses compare insurance options near Karmel Mall in Minneapolis. Get local help for auto, renters, home, and commercial insurance.",
      keywords:
        "Karmel Mall Insurance, Karmel Mall Insurance Agency, Insurance Agency in Karmel Mall, Auto Insurance Karmel Mall, Renters Insurance Karmel Mall, Insurance Near Karmel Mall, Car Insurance Karmel Mall, Commercial Insurance Karmel Mall, Insurance Agency Near Lake Street Minneapolis, Insurance Near Cedar Riverside, Minneapolis Insurance Agency, Independent Insurance Agent Minneapolis, Auto Insurance Minneapolis MN, Renters Insurance Minneapolis MN",
      priority: 0.9,
      changefreq: "monthly",
    },
  },
  {
    path: "/somali-insurance-agent-minneapolis",
    component: SomaliInsuranceAgent,
    seo: {
      title:
        "Somali Insurance Agent Minneapolis MN | Kaphy Insurance Agency",
      description:
        "Kaphy Insurance Agency has Somali-speaking insurance agents in Minneapolis, MN. Get help with auto, renters, home & commercial insurance in Somali and English. Located at Karmel Mall — free quotes.",
      keywords:
        "Somali insurance agent Minneapolis, Somali speaking insurance agent Minneapolis, Somali insurance Minneapolis, insurance agent Karmel Mall, Somali auto insurance Minneapolis, Arabic insurance agent Minneapolis, multilingual insurance agent Minneapolis, East African insurance agent Minneapolis, independent insurance agent Minneapolis MN",
      priority: 0.9,
      changefreq: "monthly",
    },
  },
  {
    path: "/minneapolis-insurance-agency",
    component: MinneapolisInsuranceAgency,
    seo: {
      title:
        "Insurance Agency Minneapolis MN | Auto & Renters | Kaphy Insurance Agency",
      description:
        "Kaphy Insurance Agency is an independent insurance agency in Minneapolis, MN. Compare auto, renters & commercial insurance from top carriers — free quotes.",
      keywords:
        "insurance agency Minneapolis, insurance agency in Minneapolis, Minneapolis insurance agency, insurance agent Minneapolis, independent insurance agency Minneapolis, independent insurance agent Minneapolis, Minneapolis MN insurance agency, insurance broker Minneapolis, local insurance agency Minneapolis, auto insurance agency Minneapolis, renters insurance agency Minneapolis, insurance agency near me",
      priority: 0.9,
      changefreq: "monthly",
    },
  },
  {
    path: "/about-us",
    component: AboutUs,
    seo: {
      title:
        "Independent Insurance Agent Minneapolis MN | About Kaphy Insurance Agency",
      description:
        "Kaphy Insurance Agency is a trusted independent insurance broker in Minneapolis, MN. We shop top carriers to get you the best insurance rates on auto, home, renters & commercial insurance.",
      keywords:
        "independent insurance agent Minneapolis, insurance broker Minneapolis, Minneapolis insurance agency, affordable insurance Minneapolis, best insurance rates Minneapolis",
      priority: 0.8,
      changefreq: "monthly",
    },
  },
  {
    path: "/location/our-locations",
    component: LocationPage,
    seo: {
      title:
        "Insurance Agency Near Me Minneapolis MN | Visit Kaphy Insurance Agency",
      description:
        "Visit Kaphy Insurance Agency at 2910 Pleasant Ave, 1st Floor, Suite 31, Minneapolis, MN 55408. Your trusted local insurance agency near you — get directions, hours, and contact info.",
      keywords:
        "insurance near me, insurance agency Minneapolis MN, Minneapolis insurance office, insurance agency near me, local insurance agent Minneapolis",
      priority: 0.7,
      changefreq: "monthly",
    },
  },
  {
    path: "/office-open-hours",
    component: OpenHours,
    seo: {
      title: "Office Hours | Kaphy Insurance Agency Minneapolis MN",
      description:
        "View Kaphy Insurance Agency office hours in Minneapolis, MN. Available Monday–Sunday to assist with auto, home, renters & commercial insurance needs.",
      keywords:
        "insurance office hours Minneapolis, insurance agency hours MN, Kaphy Insurance Agency hours, Minneapolis insurance open hours",
      priority: 0.5,
      changefreq: "yearly",
    },
  },
  {
    path: "/contact-us",
    component: ContactUs,
    seo: {
      title: "Contact Kaphy Insurance Agency | Minneapolis MN Insurance Broker",
      description:
        "Contact Kaphy Insurance Agency in Minneapolis, MN for auto insurance quotes, home insurance, renters insurance & commercial coverage. Call (651) 444-0402 or request a free quote online.",
      keywords:
        "contact insurance agent Minneapolis, insurance agency contact Minneapolis MN, get insurance quote, request a quote, Minneapolis insurance broker contact",
      priority: 0.7,
      changefreq: "yearly",
    },
  },
  {
    path: "/appointments",
    component: Appointment,
    seo: {
      title:
        "Schedule Insurance Appointment Minneapolis MN | Kaphy Insurance Agency",
      description:
        "Book an appointment with our licensed independent insurance agents in Minneapolis, MN. Get personalized advice on auto, home, renters & commercial insurance coverage.",
      keywords:
        "schedule insurance appointment Minneapolis, book insurance consultation MN, independent insurance agent appointment, Kaphy Insurance Agency appointment",
      priority: 0.6,
      changefreq: "weekly",
    },
  },
  {
    path: "/privacy",
    component: Privacy,
    seo: {
      title: "Privacy Policy | Kaphy Insurance Agency",
      description:
        "Read our privacy policy to understand how we collect, use, and protect your personal information.",
      keywords: "privacy policy, data protection, personal information",
      priority: 0.3,
      changefreq: "yearly",
    },
  },
  {
    path: "/terms",
    component: Terms,
    seo: {
      title: "Terms and Conditions | Kaphy Insurance Agency",
      description:
        "Review our terms and conditions for using our website and services.",
      keywords: "terms and conditions, terms of service, legal terms",
      priority: 0.3,
      changefreq: "yearly",
    },
  },
  // ── Auto Insurance City Landing Pages (St. Cloud Metro) ─────────────────
  // Dedicated auto insurance pages targeting "auto insurance [city] mn" searches.
  // Separate from /insurance/:citySlug (general insurance) — different search intent.
  {
    path: "/auto-insurance-st-cloud-mn",
    component: AutoInsuranceCityPage,
    seo: {
      title: "Car Insurance St Cloud MN | Kaphy Insurance Agency",
      description:
        "Compare car insurance in St. Cloud, MN. SR-22, high-risk, renters & commercial auto coverage from multiple carriers — free quotes, Somali-speaking help.",
      keywords:
        "car insurance St Cloud MN, auto insurance St Cloud MN, SR-22 insurance St Cloud MN, renters insurance St Cloud MN, commercial auto insurance St Cloud MN",
      priority: 0.9,
      changefreq: "monthly",
    },
  },
  {
    path: "/auto-insurance-sartell-mn",
    component: AutoInsuranceCityPage,
    seo: {
      title: "Auto Insurance Sartell MN | Kaphy Insurance Agency",
      description:
        "Get competitive auto insurance quotes in Sartell, MN. Kaphy Insurance Agency compares rates from top carriers — free quotes online or by phone.",
      keywords:
        "auto insurance Sartell MN, car insurance Sartell MN, cheap auto insurance Sartell, insurance Sartell Minnesota",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  {
    path: "/auto-insurance-sauk-rapids-mn",
    component: AutoInsuranceCityPage,
    seo: {
      title: "Auto Insurance Sauk Rapids MN | Kaphy Insurance Agency",
      description:
        "Compare affordable auto insurance in Sauk Rapids, MN. Kaphy Insurance Agency serves Sauk Rapids and Benton County drivers with competitive rates.",
      keywords:
        "auto insurance Sauk Rapids MN, car insurance Sauk Rapids MN, cheap auto insurance Sauk Rapids, Benton County auto insurance",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  {
    path: "/auto-insurance-waite-park-mn",
    component: AutoInsuranceCityPage,
    seo: {
      title: "Auto Insurance Waite Park MN | Kaphy Insurance Agency",
      description:
        "Get affordable auto insurance in Waite Park, MN. Kaphy Insurance Agency serves Waite Park and the St. Cloud metro with competitive rates from multiple carriers.",
      keywords:
        "auto insurance Waite Park MN, car insurance Waite Park MN, Stearns County auto insurance, car insurance near Crossroads Center",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  {
    path: "/auto-insurance-st-joseph-mn",
    component: AutoInsuranceCityPage,
    seo: {
      title: "Auto Insurance St Joseph MN | Kaphy Insurance Agency",
      description:
        "Affordable auto insurance in St. Joseph, MN. Kaphy Insurance Agency serves St. Joseph and Stearns County drivers with competitive car insurance rates.",
      keywords:
        "auto insurance St Joseph MN, car insurance St Joseph MN, Stearns County auto insurance, College of Saint Benedict insurance",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  // ── Auto Insurance City Landing Page (Minneapolis) ───────────────────────
  {
    path: "/auto-insurance-minneapolis-mn",
    component: AutoInsuranceCityPage,
    seo: {
      title:
        "Auto Insurance Minneapolis MN | Car Insurance Quotes | Kaphy Insurance Agency",
      description:
        "Independent auto insurance agency in Minneapolis, MN. Compare car insurance quotes from multiple carriers with Kaphy Insurance Agency — free, no obligation.",
      keywords:
        "auto insurance Minneapolis MN, car insurance Minneapolis, Minneapolis car insurance, Minneapolis auto insurance, auto insurance agency Minneapolis, car insurance agent Minneapolis, independent auto insurance agency Minneapolis, auto insurance broker Minneapolis, car insurance quotes Minneapolis, affordable car insurance Minneapolis, Minnesota auto insurance",
      priority: 0.9,
      changefreq: "monthly",
    },
  },

  {
    path: "/home-insurance-minneapolis-mn",
    component: HomeInsurance,
    seo: {
      title:
        "Home Insurance Minneapolis MN | Homeowners Insurance Quotes | Kaphy Insurance Agency",
      description:
        "Get home insurance quotes in Minneapolis, MN. Kaphy Insurance Agency compares homeowners coverage from multiple carriers — protect your dwelling, personal property & liability. Free quotes.",
      keywords:
        "home insurance Minneapolis MN, homeowners insurance Minneapolis, home insurance Minneapolis, Minneapolis home insurance, homeowners insurance Minneapolis MN, home insurance quotes Minneapolis, home insurance agency Minneapolis, independent home insurance agent Minneapolis, affordable home insurance Minneapolis, Minnesota home insurance",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  {
    path: "/commercial-insurance-minneapolis-mn",
    component: CommercialInsurance,
    seo: {
      title:
        "Commercial Insurance Minneapolis MN | Small Business Insurance | Kaphy Insurance Agency",
      description:
        "Get commercial insurance Minneapolis MN small business owners trust. Kaphy Insurance Agency compares general liability, commercial property, commercial auto & workers comp. Free quotes.",
      keywords:
        "commercial insurance Minneapolis MN, small business insurance Minneapolis, business insurance Minneapolis MN, commercial insurance Minneapolis, general liability insurance Minneapolis, commercial property insurance Minneapolis, workers compensation insurance Minneapolis MN, commercial auto insurance Minneapolis, Minnesota small business insurance",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  {
    path: "/commercial-truck-insurance-minnesota",
    component: TruckingInsurance,
    seo: {
      title:
        "Commercial Truck Insurance Minnesota | Owner Operator Insurance | Kaphy Insurance Agency",
      description:
        "Get commercial truck insurance quotes in Minnesota. Kaphy Insurance Agency compares truck liability, cargo insurance & physical damage for owner-operators and fleets. Free quotes.",
      keywords:
        "commercial truck insurance Minnesota, owner operator insurance Minnesota, trucking insurance Minnesota, truck insurance Minnesota, box truck insurance Minnesota, cargo van insurance Minnesota, semi truck insurance Minnesota, commercial truck insurance Minneapolis, cargo insurance Minnesota, Minnesota trucking insurance, flatbed truck insurance Minnesota, dump truck insurance Minnesota, tow truck insurance Minnesota, fleet truck insurance Minnesota, truck liability insurance Minnesota, MCS-90 insurance Minnesota, FMCSA insurance Minnesota",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  {
    path: "/life-insurance",
    component: LifeInsurance,
    seo: {
      title:
        "Life Insurance Minneapolis MN | Quote Online | Kaphy Insurance Agency",
      description:
        "Get a life insurance quote in Minneapolis, MN. Explore term life, whole life & universal coverage options through Progressive. Protect your family's financial future — no obligation.",
      keywords:
        "life insurance Minneapolis MN, life insurance Minnesota, term life insurance Minneapolis MN, term life insurance Minnesota, whole life insurance Minneapolis, whole life insurance Minnesota, universal life insurance Minnesota, life insurance quote Minneapolis MN, life insurance quote Minnesota, get life insurance quote online MN, affordable life insurance Minneapolis, life insurance for families Minnesota, life insurance for young families Minneapolis MN, life insurance agent Minneapolis MN, Progressive life insurance Minnesota, life insurance policy Minnesota, life insurance near me Minneapolis, life insurance Twin Cities MN, family financial protection Minneapolis, income replacement life insurance Minnesota",
      priority: 0.9,
      changefreq: "monthly",
    },
  },
  {
    path: "/insurance-carriers",
    component: InsuranceCarriersPage,
    seo: {
      title: "Insurance Carriers | Kaphy Insurance Agency Minneapolis MN",
      description:
        "Kaphy Insurance Agency in Minneapolis, MN works with multiple insurance carriers including Progressive, GEICO, National General, Root Insurance, Bristol West, Dairyland, ePremium, NEXT, and Stillwater. Compare auto, renters, and commercial coverage options.",
      keywords:
        "insurance carriers Minneapolis MN, insurance companies Minneapolis, Progressive insurance Minneapolis, GEICO insurance Minneapolis, National General insurance Minneapolis, Bristol West insurance Minneapolis, Dairyland insurance Minneapolis, ePremium renters insurance Minneapolis, NEXT Insurance Minneapolis, Stillwater Insurance Minneapolis, independent insurance agency Minneapolis, auto insurance carriers Minneapolis MN, renters insurance companies Minneapolis",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  {
    path: "/insurance/:citySlug",
    component: CityInsurancePage,
    seo: {
      title: "Local Insurance in Minnesota | Kaphy Insurance Agency",
      description:
        "Affordable auto, home, renters, and business insurance in Minnesota cities. Kaphy Insurance Agency — independent agents serving Brooklyn Park, Maple Grove, Plymouth, Rochester, Saint Cloud, and more.",
      keywords:
        "insurance Minnesota cities, local insurance Minnesota, auto insurance MN, home insurance MN, Kaphy Insurance Agency Minnesota",
      priority: 0.85,
      changefreq: "monthly",
    },
  },
  {
    path: "/accessibility",
    component: Accessibility,
    seo: {
      title: "Accessibility Statement | Kaphy Insurance Agency",
      description:
        "Our commitment to making our website accessible to all users, including those with disabilities.",
      keywords:
        "accessibility statement, website accessibility, ADA compliance",
      priority: 0.3,
      changefreq: "yearly",
    },
  },
  // ── Insurance Guides / Blog Section ─────────────────────────────────────
  {
    path: "/guides",
    component: GuidesPage,
    seo: {
      title:
        "Insurance Guides & Resources | Kaphy Insurance Agency Minneapolis",
      description:
        "Free insurance guides for Minnesota residents. Learn about auto, home, renters, and commercial insurance — coverage, costs, and how to save money in the Twin Cities.",
      keywords:
        "insurance guides minnesota, auto insurance guide, renters insurance guide, homeowners insurance guide, commercial insurance guide, minneapolis insurance tips, insurance resources mn",
      priority: 0.85,
      changefreq: "weekly",
    },
  },
  {
    path: "/guides/:slug",
    component: GuideArticlePage,
    seo: {
      title: "Insurance Guide | Kaphy Insurance Agency Minneapolis",
      description:
        "Read our expert insurance guide for Minnesota residents. Get accurate, local advice on coverage, costs, and how to get the best insurance rates.",
      keywords:
        "insurance guide minnesota, insurance advice minneapolis, insurance tips mn",
      priority: 0.8,
      changefreq: "monthly",
    },
  },
];

/**
 * Auth Routes Configuration
 * These routes are for authentication
 */
export const authRoutes = [
  {
    path: "/api/auth/login",
    component: LoginPage,
    seo: {
      title: "Login | Kaphy Insurance Agency",
      description:
        "Login to access your insurance dashboard and manage your policies.",
      keywords: "login, sign in, customer portal",
      priority: 0.2,
      changefreq: "yearly",
      robots: "noindex, nofollow", // Auth pages should not be indexed
      includeInSitemap: false, // Exclude from sitemap — noindex pages must not appear in sitemap
    },
  },
  {
    path: "/api/auth/logout",
    component: Logout,
    seo: {
      title: "Logout | Kaphy Insurance Agency",
      description: "Logout from your account.",
      keywords: "logout, sign out",
      priority: 0.1,
      changefreq: "yearly",
      robots: "noindex, nofollow",
      includeInSitemap: false, // Exclude from sitemap
    },
  },
];

/**
 * Protected Routes Configuration
 * These routes require authentication and/or admin privileges
 */
export const protectedRoutes = [
  {
    path: "/dashboard",
    component: Dashboard,
    protected: true,
    adminOnly: true,
    seo: {
      title: "Admin Dashboard | Kaphy Insurance Agency",
      description:
        "Admin dashboard for managing quotes, appointments, and users.",
      keywords: "admin dashboard, insurance management",
      priority: 0.0,
      changefreq: "daily",
      robots: "noindex, nofollow", // Admin pages should never be indexed
      includeInSitemap: false, // Exclude from sitemap
    },
  },
];

/**
 * 404 Not Found Route
 */
export const notFoundRoute = {
  path: "*",
  component: NotFound,
  seo: {
    title: "404 - Page Not Found | Kaphy Insurance Agency",
    description: "The page you are looking for could not be found.",
    robots: "noindex, nofollow",
    includeInSitemap: false,
  },
};

/**
 * All Routes Combined
 * Useful for mapping all routes
 */
export const allRoutes = [
  ...publicRoutes,
  ...authRoutes,
  ...protectedRoutes,
  notFoundRoute,
];

/**
 * Routes for Sitemap Generation
 * Filters out routes that should not be included in sitemap
 */
export const sitemapRoutes = [
  ...publicRoutes,
  ...authRoutes.filter((route) => route.seo?.includeInSitemap !== false),
  ...protectedRoutes.filter((route) => route.seo?.includeInSitemap !== false),
].filter((route) => route.seo?.includeInSitemap !== false);

const routeConfig = {
  publicRoutes,
  authRoutes,
  protectedRoutes,
  notFoundRoute,
  allRoutes,
  sitemapRoutes,
};

export default routeConfig;
